import { computed, MaybeRefOrGetter, toValue } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import {
  HireCustomFieldsAnsweredBy,
  useHireCustomFieldSections,
} from '@/components/Hire/composables/useHireCustomFieldSections'
import { useHireRecruiter } from '@/components/Hire/composables/useHireRecruiter'
import { FieldableFormCustomFieldsQuery } from '@/components/Fieldable/graphql/queries'
import { graphql } from '@/gql'
import { AccountType, TypeSupportingCustomFieldValues } from '@/gql/graphql'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'

// Everything needed to show a hire's custom fields read-only, loaded from the hire ID alone.
// The sections show every answer unless answeredBy picks one side.
export function useHireCustomFields(
  hireId: MaybeRefOrGetter<string>,
  answeredBy?: MaybeRefOrGetter<HireCustomFieldsAnsweredBy | undefined>,
) {
  const { isCompany } = useAccountCapabilities()
  const {
    viewerIsSupplier,
    hasRecruiterManagingWorkers,
    loading: roleLoading,
    error: roleError,
  } = useHireRecruiter(hireId)

  // The same viewers as on the hire page: a company that is not the supplier.
  // A supplier looks like a client until the role has loaded, so wait for it.
  const viewingAsClient = computed(
    () => isCompany.value && !roleLoading.value && !roleError.value && !viewerIsSupplier.value,
  )

  const {
    result,
    loading: hireLoading,
    error: hireError,
  } = useQuery(
    graphql(`
      query HireCustomFieldAnswers($id: ID!) {
        hire(id: $id) {
          ...HireCustomFieldSections
        }
      }
    `),
    () => ({ id: toValue(hireId) }),
    { clientId: 'publicApi', enabled: viewingAsClient },
  )

  const {
    result: fieldsResult,
    loading: fieldsLoading,
    error: fieldsError,
  } = useQuery(
    FieldableFormCustomFieldsQuery,
    () => ({
      viewerAccount: currentAccount.globalId,
      accounts: [currentAccount.globalId],
      appliesTo: [TypeSupportingCustomFieldValues.Hire],
      first: PER_PAGE_MAXIMUM,
    }),
    { clientId: 'publicApi', enabled: viewingAsClient },
  )

  const {
    sections: allSections,
    companySection,
    workerSection,
  } = useHireCustomFieldSections({
    fields: () => fieldsResult.value?.customFields?.data ?? [],
    hire: () => result.value?.hire,
    recruiterManagesWorker: hasRecruiterManagingWorkers,
  })

  const sections = computed(() => {
    const only = toValue(answeredBy)

    if (only === undefined) return allSections.value

    return [only === AccountType.Worker ? workerSection.value : companySection.value]
  })

  const hasSections = computed(() =>
    sections.value.some((section) => section.fields.length > 0 || section.values.length > 0),
  )

  const loading = computed(() => roleLoading.value || hireLoading.value || fieldsLoading.value)
  const error = computed(() => roleError.value ?? hireError.value ?? fieldsError.value ?? null)

  return { sections, hasSections, loading, error }
}
